import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { normalizeTarget, validateTarget } from "../lib/orderValidation";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { FloatingWA } from "../components/FloatingWA";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowRight, CircleDollarSign, ClipboardList, Info, Link2, ListFilter, Loader2, Save, Search, Share2, ShoppingCart, UserRound, Wallet } from "lucide-react";
import { FaDiscord, FaFacebookF, FaInstagram, FaTelegramPlane, FaYoutube } from "react-icons/fa";
import { toast } from "sonner";

const serviceCategoryKeywords = [
  ["Followers", ["follower", "followers", "subscriber", "subscribers"]],
  ["Likes", ["like", "likes"]],
  ["Views", ["view", "views"]],
  ["Shares", ["share", "shares"]],
  ["Saves", ["save", "saves", "bookmark", "bookmarks"]],
  ["Comments", ["comment", "comments", "reply", "replies"]],
];

const matchServiceCategory = (value) => {
  const normalized = String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return serviceCategoryKeywords.find(([, keywords]) =>
    keywords.some((keyword) => new RegExp(`\\b${keyword}\\b`).test(normalized))
  )?.[0];
};

const getServiceCategory = (service) => {
  return matchServiceCategory(service.service_category)
    || matchServiceCategory(service.category_name)
    || matchServiceCategory(service.name)
    || null;
};

const sortServiceCategories = (names) => [...names].sort((a, b) =>
  serviceCategoryKeywords.findIndex(([name]) => name === a)
    - serviceCategoryKeywords.findIndex(([name]) => name === b)
);

const getAvailableServiceCategories = (services) => sortServiceCategories(
  new Set(services.map(getServiceCategory).filter(Boolean))
);

const getCategorizedServices = (services) => services.filter((service) => getServiceCategory(service));
const ORDER_DRAFT_STORAGE_KEY = "azmiey-new-order-draft";

const readOrderDraft = () => {
  try {
    const draft = JSON.parse(window.localStorage.getItem(ORDER_DRAFT_STORAGE_KEY) || "null");
    return draft && typeof draft === "object" ? draft : null;
  } catch {
    return null;
  }
};

const getPlatformSlug = (category) => (category?.slug || category?.icon || category?.name || "").toLowerCase().replace(/\s+/g, "");

const PlatformLogo = ({ slug, className = "" }) => {
  const normalizedSlug = (slug || "").toLowerCase();
  if (normalizedSlug.includes("tiktok")) {
    const path = "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z";
    return (
      <span className={`platform-tiktok-mark ${className}`} aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d={path} fill="#25F4EE" transform="translate(-.7 .7)" />
          <path d={path} fill="#FE2C55" transform="translate(.7 -.7)" />
          <path d={path} fill="#fff" />
        </svg>
      </span>
    );
  }

  const brandIcons = {
    instagram: [FaInstagram, "#E4405F"],
    youtube: [FaYoutube, "#FF0000"],
    facebook: [FaFacebookF, "#1877F2"],
    discord: [FaDiscord, "#5865F2"],
    telegram: [FaTelegramPlane, "#26A5E4"],
  };
  const [Icon, color] = Object.entries(brandIcons).find(([name]) => normalizedSlug.includes(name))?.[1] || [FaInstagram, "#534AB7"];
  return <Icon className={`platform-brand-logo ${className}`} style={{ color }} aria-hidden="true" />;
};

const FieldLabel = ({ children, icon: Icon }) => (
  <div className="order-field-label">
    <span>{children}</span>
    {Icon && <Icon className="h-4 w-4 text-[#7C3AED]" />}
  </div>
);

const InfoBox = ({ children, testid, className }) => (
  <div data-testid={testid} className={`order-info-box ${className || "truncate"}`}>{children}</div>
);

export default function PublicPanel() {
  const { user, loading: authLoading, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [accountOrders, setAccountOrders] = useState(null);
  const [accountOrdersStatus, setAccountOrdersStatus] = useState("loading");
  const [categoryId, setCategoryId] = useState("");
  const [serviceCategory, setServiceCategory] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [search, setSearch] = useState("");
  const [quantity, setQuantity] = useState("");
  const [target, setTarget] = useState("");
  const [orderType, setOrderType] = useState("new");
  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [draftToRestore] = useState(readOrderDraft);
  const draftRestorePending = useRef(Boolean(draftToRestore));
  const draftRestoreServiceId = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([api.get("/settings"), api.get("/categories")]);
        setSettings(s.data);
        setCategories(c.data);
        if (c.data.length > 0) {
          const savedPlatform = c.data.find((category) =>
            (draftToRestore?.platformId && String(category.id) === String(draftToRestore.platformId))
            || (draftToRestore?.platformSlug && getPlatformSlug(category) === draftToRestore.platformSlug)
          );
          const defaultPlatform = c.data.find((category) => getPlatformSlug(category).includes("tiktok")) || c.data[0];
          setCategoryId((savedPlatform || defaultPlatform).id);
          if (draftToRestore && !savedPlatform) draftRestorePending.current = false;
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [draftToRestore]);

  useEffect(() => {
    if (!user || user === false || user.role === "admin") {
      setAccountOrders(null);
      setAccountOrdersStatus("unavailable");
      return;
    }
    setAccountOrdersStatus("loading");
    let active = true;
    api.get("/me/orders")
      .then(({ data }) => { if (active) { setAccountOrders(data); setAccountOrdersStatus("loaded"); } })
      .catch(() => { if (active) { setAccountOrders(null); setAccountOrdersStatus("error"); } });
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    setServices([]);
    setServiceId("");
    setServiceCategory("");
    setSearch("");
    draftRestoreServiceId.current = null;
    if (!categoryId) return;
    let active = true;
    (async () => {
      setServicesLoading(true);
      try {
        const { data } = await api.get("/services", { params: { category_id: categoryId } });
        if (active) {
          const categorizedServices = getCategorizedServices(data);
          const availableCategories = getAvailableServiceCategories(categorizedServices);
          const platform = categories.find((category) => String(category.id) === String(categoryId));
          const restoringThisPlatform = draftRestorePending.current && draftToRestore && (
            (draftToRestore.platformId && String(draftToRestore.platformId) === String(categoryId))
            || (draftToRestore.platformSlug && draftToRestore.platformSlug === getPlatformSlug(platform))
          );
          const restoredService = restoringThisPlatform
            ? categorizedServices.find((service) => String(service.id) === String(draftToRestore.serviceId))
            : null;
          const restoredCategory = restoredService && getServiceCategory(restoredService);
          const savedCategory = restoringThisPlatform && availableCategories.find((name) =>
            name.toLowerCase() === String(draftToRestore.category || "").toLowerCase()
          );
          setServices(categorizedServices);
          const nextServiceCategory = restoredCategory || savedCategory
            || availableCategories.find((name) => name.toLowerCase() === "followers")
            || availableCategories[0]
            || "";
          setServiceCategory(nextServiceCategory);
          if (restoringThisPlatform) {
            setTarget(typeof draftToRestore.link === "string" ? draftToRestore.link : "");
            setQuantity(draftToRestore.quantity == null ? "" : String(draftToRestore.quantity));
            if (restoredService && getServiceCategory(restoredService) === nextServiceCategory) {
              draftRestoreServiceId.current = String(restoredService.id);
              setServiceId(restoredService.id);
            } else {
              draftRestorePending.current = false;
            }
          } else {
            draftRestorePending.current = false;
          }
        }
      } catch {
        if (active) {
          setServices([]);
          setServiceId("");
        }
      } finally {
        if (active) setServicesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [categoryId, categories, draftToRestore]);

  const serviceCategories = useMemo(() => {
    return getAvailableServiceCategories(services);
  }, [services]);

  const categoryServices = useMemo(
    () => services.filter((service) => getServiceCategory(service) === serviceCategory),
    [services, serviceCategory]
  );
  const filteredServices = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return categoryServices;
    return categoryServices.filter((service) =>
      String(service.service_code).toLowerCase().includes(query)
      || service.name.toLowerCase().includes(query)
    );
  }, [categoryServices, search]);

  const selectedService = useMemo(
    () => services.find((service) => service.id === serviceId),
    [services, serviceId]
  );
  useEffect(() => {
    const restoreDraft = selectedService
      && draftRestoreServiceId.current === String(selectedService.id)
      && draftToRestore;
    if (restoreDraft) {
      draftRestoreServiceId.current = null;
      setQuantity(draftToRestore.quantity == null ? String(selectedService.min_qty || "") : String(draftToRestore.quantity));
      setTarget(typeof draftToRestore.link === "string" ? draftToRestore.link : "");
      draftRestorePending.current = false;
      return;
    }
    setQuantity(selectedService?.min_qty ? String(selectedService.min_qty) : "");
    setTarget("");
  }, [selectedService, draftToRestore]);

  const numericQuantity = Number(quantity);
  const quantityValid = Boolean(selectedService && quantity)
    && Number.isInteger(numericQuantity)
    && numericQuantity >= 1
    && numericQuantity >= Number(selectedService?.min_qty)
    && numericQuantity <= Number(selectedService?.max_qty);
  const quantityError = quantity && selectedService && !quantityValid
    ? `Quantity mesti antara ${selectedService.min_qty} - ${selectedService.max_qty}`
    : "";
  const total = selectedService && Number.isFinite(numericQuantity) && numericQuantity >= 0
    ? Number(((Number(selectedService.price_per_1000) * numericQuantity) / 1000).toFixed(2))
    : 0;
  const orderCount = accountOrders?.length;
  const totalSpend = accountOrders?.reduce((sum, order) => sum + Number(order.cost || 0), 0);
  const accountName = user?.name || user?.email || "-";
  const accountSummaryLoading = authLoading || (user && user !== false && user.role !== "admin" && accountOrdersStatus === "loading");
  const selectedPlatform = categories.find((category) => category.id === categoryId);
  const platformOptions = ["tiktok", "instagram", "youtube", "facebook", "discord", "telegram"].map((slug) => ({
    slug,
    category: categories.find((category) => getPlatformSlug(category).includes(slug)),
  }));

  const submitOrder = async () => {
    if (orderLoading || servicesLoading || !selectedService) return;
    const normalizedTarget = normalizeTarget(target);
    const targetError = validateTarget(selectedService, normalizedTarget);
    if (targetError) return toast.error(targetError);
    if (!Number.isInteger(numericQuantity) || numericQuantity < selectedService.min_qty || numericQuantity > selectedService.max_qty) {
      return toast.error(`Quantity mesti antara ${selectedService.min_qty} - ${selectedService.max_qty}`);
    }
    if (!user || user === false || user.role === "admin") return toast.error("Sila log masuk untuk membuat pesanan.");

    setOrderLoading(true);
    try {
      const currentUser = refresh ? await refresh() : user;
      if (!currentUser || currentUser === false || currentUser.role === "admin") {
        toast.error("Sesi anda telah tamat. Sila log masuk semula.");
        return;
      }
      if (total > Number(currentUser.balance || 0)) {
        toast.error("Baki Wallet tidak mencukupi. Sila top up sebelum membuat pesanan.");
        return;
      }
      await api.post("/orders", { service_id: selectedService.id, quantity: numericQuantity, target_link: normalizedTarget });
      await refresh();
      toast.success("Order berjaya! Order telah dihantar ke provider.");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || "Gagal buat order");
    } finally {
      setOrderLoading(false);
    }
  };

  const saveDraft = () => {
    try {
      window.localStorage.setItem(ORDER_DRAFT_STORAGE_KEY, JSON.stringify({
        platformId: categoryId,
        platformSlug: getPlatformSlug(selectedPlatform),
        category: serviceCategory,
        serviceId,
        link: target,
        quantity,
      }));
      toast.success("Order draft saved.");
    } catch {
      toast.error("Could not save the order draft.");
    }
  };

  const sharePage = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: "AZMIEY STORE", url });
        toast.success("Page shared.");
      } catch (error) {
        if (error.name !== "AbortError") toast.error("Could not share this page.");
      }
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        throw new Error("Clipboard API unavailable");
      }
    } catch {
      try {
        const input = document.createElement("textarea");
        input.value = url;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(input);
        if (!copied) throw new Error("Copy command failed");
      } catch {
        toast.error("Could not copy the page URL.");
        return;
      }
    }
    toast.success("Page URL copied.");
  };

  return (
    <div className="order-page min-h-screen text-[#25252B]">
      <Header settings={settings} />

      <main className="order-shell fade-up">
        <section className="account-summary" aria-label="Account summary">
          <a className="account-summary-row" href={user && user !== false ? "/dashboard" : "/login"}>
            <span className="summary-icon"><UserRound /></span>
            <span className="summary-copy"><span className="summary-label">Username</span><strong>{accountSummaryLoading ? <span className="summary-skeleton" /> : accountName}</strong></span>
          </a>
          <a className="account-summary-row" href={user && user !== false ? "/dashboard" : "/login"}>
            <span className="summary-icon"><CircleDollarSign /></span>
            <span className="summary-copy"><span className="summary-label">Total spent</span><strong>{accountSummaryLoading ? <span className="summary-skeleton" /> : accountOrdersStatus === "error" || totalSpend == null ? "-" : `RM ${totalSpend.toFixed(2)}`}</strong></span>
          </a>
          <a className="account-summary-row" href={user && user !== false ? "/orders" : "/login"}>
            <span className="summary-icon"><ClipboardList /></span>
            <span className="summary-copy"><span className="summary-label">Total order</span><strong>{accountSummaryLoading ? <span className="summary-skeleton" /> : accountOrdersStatus === "error" || orderCount == null ? "-" : orderCount}</strong></span>
          </a>
          <a className="account-summary-row" href={user && user !== false ? "/topup" : "/login"}>
            <span className="summary-icon"><Wallet /></span>
            <span className="summary-copy"><span className="summary-label">Balance</span><strong>{accountSummaryLoading ? <span className="summary-skeleton" /> : user && user !== false ? `RM ${Number(user.balance || 0).toFixed(2)}` : "-"}</strong></span>
          </a>
        </section>

        <section className="order-card order-platform-card">
          <div className="order-card-heading">
            <div><span className="heading-accent" /><h2>Choose platform</h2></div>
            <span className="order-muted">6 available</span>
          </div>
          <div className="platform-grid" aria-label="Platform selection">
            {platformOptions.map(({ slug, category }) => (
              <button
                key={slug}
                type="button"
                data-testid={`platform-${slug}`}
                disabled={!category}
                onClick={() => {
                  if (category && categoryId !== category.id) {
                    draftRestorePending.current = false;
                    draftRestoreServiceId.current = null;
                    setCategoryId(category.id);
                  }
                }}
                className={`platform-button ${categoryId === category?.id ? "selected" : ""}`}
                title={category?.name || `${slug} unavailable`}
              >
                <PlatformLogo slug={slug} />
                <span>{slug === "youtube" ? "YouTube" : slug === "tiktok" ? "TikTok" : slug[0].toUpperCase() + slug.slice(1)}</span>
              </button>
            ))}
            {!loading && categories.length === 0 && <div className="order-empty">No platforms available.</div>}
          </div>
        </section>

        <div className="order-type-toggle" role="tablist" aria-label="Order type">
          <button type="button" role="tab" aria-selected={orderType === "new"} className={`new-order-action ${orderType === "new" ? "active" : ""}`} onClick={() => setOrderType("new")}>
            <ShoppingCart className="h-5 w-5" /> <span>New Order</span><ArrowRight className="order-action-arrow" />
          </button>
          <button type="button" role="tab" aria-selected={orderType === "mass"} className={`mass-order-action ${orderType === "mass" ? "active" : ""}`} onClick={() => setOrderType("mass")}>
            <ListFilter className="h-5 w-5" /> <span>Mass Order</span><ArrowRight className="order-action-arrow" />
          </button>
        </div>

        {orderType === "new" ? (
          <section className="order-card order-form-card">
            <div className="order-form-section service-form-section">
              <FieldLabel>Kategori</FieldLabel>
              <Select value={serviceCategory} onValueChange={(value) => {
                setServiceCategory(value);
                setServiceId("");
                setQuantity("");
                setTarget("");
                setSearch("");
              }} disabled={!categoryId || servicesLoading || serviceCategories.length === 0}>
                <SelectTrigger data-testid="category-select" className="order-select category-select">
                  <SelectValue placeholder={servicesLoading ? "Loading categories..." : "Select category"}>
                    {serviceCategory && <span className="category-select-value"><PlatformLogo slug={getPlatformSlug(selectedPlatform)} /><span title={serviceCategory}>{serviceCategory}</span></span>}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="order-select-content max-h-[320px]">
                    {serviceCategories.map((name) => <SelectItem key={name} value={name} className="category-option"><PlatformLogo slug={getPlatformSlug(selectedPlatform)} /><span className="category-option-name">{name}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="order-form-section service-form-section">
              <FieldLabel>Service</FieldLabel>
              <div className="order-search-wrap service-search-wrap">
                <Search className="h-5 w-5" />
                <Input data-testid="search-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cari ID atau nama servis" className="order-input order-search-input" disabled={!serviceCategory} />
              </div>
              <Select value={serviceId} onValueChange={setServiceId} disabled={!serviceCategory || servicesLoading}>
                <SelectTrigger data-testid="service-select" className="order-select service-select">
                  <SelectValue placeholder={servicesLoading ? "Loading services..." : "Select service"}>
                    {selectedService && <span className="service-select-value" title={`${selectedService.service_code} - ${selectedService.name} [${selectedService.max_qty}] - RM ${Number(selectedService.price_per_1000).toFixed(2)} per 1000`}>{selectedService.service_code} - {selectedService.name} [{selectedService.max_qty}] - RM {Number(selectedService.price_per_1000).toFixed(2)} per 1000</span>}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="order-select-content service-select-content max-h-[320px]">
                  {filteredServices.map((service) => (
                    <SelectItem key={service.id} value={service.id} data-testid={`service-option-${service.service_code}`} className="service-option">
                      <span className="service-option-line" title={`${service.service_code} - ${service.name} [${service.max_qty}] - RM ${Number(service.price_per_1000).toFixed(2)} per 1000`}>{service.service_code} - {service.name} [{service.max_qty}] - RM {Number(service.price_per_1000).toFixed(2)} per 1000</span>
                    </SelectItem>
                  ))}
                  {!servicesLoading && categoryServices.length === 0 && <div className="px-3 py-5 text-center text-sm text-[#70747C]">Tiada servis tersedia</div>}
                  {!servicesLoading && categoryServices.length > 0 && filteredServices.length === 0 && <div className="px-3 py-5 text-center text-sm text-[#70747C]">Tiada servis sepadan</div>}
                </SelectContent>
              </Select>
            </div>

            <div className="order-info-grid">
              <div>
                <FieldLabel icon={Info}>Description</FieldLabel>
                <InfoBox testid="description" className="order-description-box">
                  <p>{selectedService?.description?.trim() || "No description available"}</p>
                </InfoBox>
              </div>
            </div>

            <div className="order-form-section">
              <FieldLabel>Link</FieldLabel>
              <div className="order-input-with-icon"><Link2 className="h-5 w-5" /><Input data-testid="order-target-input" value={target} onChange={(event) => setTarget(event.target.value)} placeholder="Paste your link here" className="order-input" /></div>
            </div>

            <div className="order-form-section">
              <FieldLabel>Quantity</FieldLabel>
              <Input data-testid="order-qty-input" type="number" value={quantity} min={selectedService?.min_qty} max={selectedService?.max_qty} aria-invalid={Boolean(quantityError)} onChange={(event) => setQuantity(event.target.value)} placeholder="Enter quantity" className="order-input" />
              {quantityError && <div className="order-quantity-error" role="alert">{quantityError}</div>}
              <div className="order-helper">Min: {selectedService?.min_qty ?? "-"} <span>·</span> Max: {selectedService?.max_qty ?? "-"}</div>
            </div>

            <div className="order-summary">
              <div className="order-summary-total"><span>Total</span><strong>RM {total.toFixed(2)}</strong></div>
            </div>

            <div className="order-form-actions">
              <button data-testid="order-save-draft" type="button" onClick={saveDraft} className="order-form-action">
                <Save className="h-4 w-4" /> Save
              </button>
              <button data-testid="order-share-page" type="button" onClick={sharePage} className="order-form-action">
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>

            <button data-testid="order-pay-btn" type="button" disabled={!selectedService || !quantityValid || servicesLoading || orderLoading} onClick={submitOrder} className="place-order-button">
              {orderLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />} {orderLoading ? "Memproses..." : "Buat Pesanan"}
            </button>
          </section>
        ) : (
          <section className="order-card mass-order-placeholder"><ListFilter className="h-8 w-8 text-[#6D28D9]" /><h2>Mass Order</h2><p>Mass order tools are available from the existing order workflow.</p><button type="button" onClick={() => setOrderType("new")} className="dark-order-button">Continue with New Order</button></section>
        )}
      </main>

      <Footer settings={settings} />
      <FloatingWA number={settings?.wa_number} />
    </div>
  );
}